import axios, { AxiosError } from 'axios';

/**
 * Decorates Axios errors with stable, grouping-friendly metadata before they propagate
 * to Datadog Error Tracking.
 *
 * Without this, every failing endpoint gets lumped into a single "AxiosError" issue in
 * Datadog because the default message ("Request failed with status code 500", "Network
 * Error", "timeout of 10000ms exceeded") is identical across endpoints. By rewriting
 * `error.message` with a `[METHOD NORMALIZED_PATH → STATUS]` prefix and attaching
 * structured properties (endpoint, method, status, errCode), Datadog's similarity
 * grouping produces one issue per (endpoint, status) — which is triageable.
 *
 */

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_REGEX = /^\d+$/;
const TAGGED_FLAG = '__ddTagged';

interface DecoratedAxiosError extends AxiosError {
  [TAGGED_FLAG]?: boolean;
  endpoint?: string;
  errCode?: string;
  method?: string;
  originalMessage?: string;
  status?: number;
}

/**
 * Extracts a path from a raw URL and replaces numeric/UUID segments with placeholders so
 * `/api/v3/bid_requests/123/rounds/4f3a91c2-1d4e-4c2a-b1aa-9d2e1b8a6f3c` becomes
 * `/api/v3/bid_requests/:id/rounds/:uuid`. Query strings are dropped — they're not part
 * of the fingerprint we want Datadog to group on.
 */
export const normalizeAxiosErrorPath = (rawUrl: string | null | undefined): string => {
  if (!rawUrl) return '';

  // Use URL with a placeholder base so both absolute and relative URLs resolve to a pathname.
  let pathname: string;
  try {
    ({ pathname } = new URL(rawUrl, 'http://placeholder.local'));
  } catch {
    pathname = rawUrl.split('?')[0];
  }

  let idCount = 0;
  let uuidCount = 0;

  const normalizedSegments = pathname.split('/').map(segment => {
    if (!segment) return segment;
    if (UUID_REGEX.test(segment)) {
      uuidCount += 1;
      return uuidCount === 1 ? ':uuid' : `:uuid${uuidCount}`;
    }
    if (NUMERIC_REGEX.test(segment)) {
      idCount += 1;
      return idCount === 1 ? ':id' : `:id${idCount}`;
    }
    return segment;
  });

  return normalizedSegments.join('/');
};

const resolveStatusLabel = (error: AxiosError): { errCode: string | undefined; label: string; status: number | undefined } => {
  const status = error.response?.status;
  const errCode = error.code;

  if (errCode === 'ECONNABORTED') {
    return { errCode, label: 'timeout', status };
  }
  if (errCode === 'ERR_NETWORK' || (status === undefined && !error.response)) {
    return { errCode: errCode ?? 'ERR_NETWORK', label: 'network', status };
  }
  if (typeof status === 'number') {
    return { errCode, label: String(status), status };
  }

  return { errCode, label: 'unknown', status };
};

/**
 * Mutates an Axios error in-place with stable grouping metadata and a normalized message
 * prefix. Returns the same error for convenient chaining.
 *
 * No-op when:
 *  - the value is falsy
 *  - the value is a cancellation (axios.isCancel)
 *  - the value is not an Axios error
 *  - the error has already been tagged (avoids double-prefixing on retries)
 */
export const decorateAxiosError = (error: unknown): unknown => {
  if (!error) return error;
  if (axios.isCancel(error)) return error;

  // Use both the type guard and the flag-on-error fallback because some upstream code paths
  // construct AxiosError-shaped objects without going through the constructor.
  const isAxios = axios.isAxiosError(error) || (error as { isAxiosError?: boolean })?.isAxiosError === true;
  if (!isAxios) return error;

  const decorated = error as DecoratedAxiosError;
  if (decorated[TAGGED_FLAG]) return decorated;

  const method = decorated.config?.method?.toUpperCase() ?? 'UNKNOWN';
  const endpoint = normalizeAxiosErrorPath(decorated.config?.url);
  const { errCode, label, status } = resolveStatusLabel(decorated);

  const prefix = `[${method} ${endpoint || '<unknown>'} → ${label}]`;
  const originalMessage = decorated.message || 'AxiosError';

  decorated.originalMessage = originalMessage;
  decorated.message = `${prefix} ${originalMessage}`;
  decorated.endpoint = endpoint;
  decorated.method = method;
  decorated.status = status;
  decorated.errCode = errCode;
  decorated[TAGGED_FLAG] = true;

  return decorated;
};

let installed = false;

/**
 * Registers a global Axios response interceptor that decorates rejected errors before they
 * propagate to consumers (and to Datadog Error Tracking). Idempotent — safe to call more
 * than once.
 */
export const installAxiosErrorTagging = (): void => {
  if (installed) return;

  axios.interceptors.response.use(
    undefined,
    error => Promise.reject(decorateAxiosError(error))
  );

  installed = true;
};

/**
 * Test-only escape hatch to reset the install guard between test cases. Do not call from
 * production code.
 */
export const __resetAxiosErrorTaggingForTests = (): void => {
  installed = false;
};
